import React, { useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { User, Lock, Shield, Mail } from 'lucide-react';

const UserProfile = () => {
  const { user } = useAuth();
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validation
    if (passwordForm.new_password.length < 8) {
      toast.error('Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        },
        { withCredentials: true }
      );
      
      toast.success('Mot de passe modifié avec succès');
      
      // Reset form
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      const message = error.response?.data?.detail || 'Erreur lors du changement de mot de passe';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
          <User className="h-6 w-6 text-amber-600" />
          Mon Profil
        </h1>
        <p className="text-stone-600 mt-1">Gérez vos informations personnelles et votre sécurité</p>
      </div>

      {/* User Info Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-amber-600" />
            Informations du compte
          </CardTitle>
          <CardDescription>Vos informations personnelles</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-stone-600">Nom</Label>
              <p className="font-medium text-stone-800">{user?.name || 'Non défini'}</p>
            </div>
            <div>
              <Label className="text-stone-600">Email</Label>
              <p className="font-medium text-stone-800">{user?.email}</p>
            </div>
          </div>
          
          {user?.role === 'admin' && (
            <Alert className="bg-amber-50 border-amber-200">
              <Shield className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 ml-2">
                Vous êtes administrateur de cette application
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Change Password Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-amber-600" />
            Changer le mot de passe
          </CardTitle>
          <CardDescription>Mettez à jour votre mot de passe de connexion</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label htmlFor="current_password">Mot de passe actuel *</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="Entrez votre mot de passe actuel"
                required
                disabled={loading}
                data-testid="input-current-password"
              />
            </div>
            
            <div>
              <Label htmlFor="new_password">Nouveau mot de passe *</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Au moins 8 caractères"
                required
                disabled={loading}
                data-testid="input-new-password"
              />
              {passwordForm.new_password && passwordForm.new_password.length < 8 && (
                <p className="text-xs text-rose-600 mt-1">⚠️ Le mot de passe doit contenir au moins 8 caractères</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="confirm_password">Confirmer le nouveau mot de passe *</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder="Confirmez votre nouveau mot de passe"
                required
                disabled={loading}
                data-testid="input-confirm-password"
              />
              {passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
                <p className="text-xs text-rose-600 mt-1">⚠️ Les mots de passe ne correspondent pas</p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPasswordForm({ current_password: '', new_password: '', confirm_password: '' })}
                disabled={loading}
              >
                Réinitialiser
              </Button>
              <Button
                type="submit"
                className="bg-amber-600 hover:bg-amber-700"
                disabled={loading || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
                data-testid="btn-change-password"
              >
                {loading ? 'Enregistrement...' : 'Changer le mot de passe'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserProfile;
